import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { okaoAxios } from '../../helpers/axios'
import { RootState, store } from '../../redux/store'
import { MenuCandidate, MissingMenuRestaurant } from './types'

export interface IMissingMenuQueueSlice {
  items: MissingMenuRestaurant[]
  status: string
  count: number
  pageSize: number
  page: number
}

const initialState: IMissingMenuQueueSlice = {
  items: [],
  status: 'idle',
  count: 0,
  pageSize: 20,
  page: 0,
}

export const fetchMissingMenuQueue = createAsyncThunk(
  'missingMenuQueue/fetch',
  async (page: number) => {
    const auth = store.getState().secure.accessToken ?? ''
    const size = store.getState().missingMenuQueue.pageSize
    const response = await okaoAxios(auth).get(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/missing-menus/${page}?size=${size}`,
    )
    return response
  },
)

export const scanMissingMenuWebsite = createAsyncThunk(
  'missingMenuQueue/scan',
  async (restaurantId: string) => {
    const auth = store.getState().secure.accessToken ?? ''
    const response = await okaoAxios(auth).post(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/missing-menus/${restaurantId}/scan`,
    )
    return { restaurantId, data: response.data }
  },
)

export const acceptMenuCandidate = createAsyncThunk(
  'missingMenuQueue/acceptCandidate',
  async ({ restaurantId, candidateId }: { restaurantId: string; candidateId: string }) => {
    const auth = store.getState().secure.accessToken ?? ''
    const response = await okaoAxios(auth).put(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/missing-menus/candidates/${candidateId}/accept`,
    )
    return { restaurantId, candidate: response.data as MenuCandidate }
  },
)

export const rejectMenuCandidate = createAsyncThunk(
  'missingMenuQueue/rejectCandidate',
  async ({ restaurantId, candidateId }: { restaurantId: string; candidateId: string }) => {
    const auth = store.getState().secure.accessToken ?? ''
    const response = await okaoAxios(auth).put(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/missing-menus/candidates/${candidateId}/reject`,
    )
    return { restaurantId, candidate: response.data as MenuCandidate }
  },
)

export const autoProcessAllMissingMenus = createAsyncThunk(
  'missingMenuQueue/autoProcessAll',
  async () => {
    const auth = store.getState().secure.accessToken ?? ''
    const response = await okaoAxios(auth).post(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/missing-menus/auto-process-all`,
    )
    return response.data
  },
)

export const missingMenuQueueSlice = createSlice({
  name: 'missingMenuQueue',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMissingMenuQueue.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchMissingMenuQueue.fulfilled, (state, action) => {
        state.items = action.payload.data.result ?? []
        state.count = action.payload.data.count ?? 0
        state.status = 'idle'
      })
      .addCase(fetchMissingMenuQueue.rejected, (state) => {
        state.status = 'error'
      })
      .addCase(scanMissingMenuWebsite.fulfilled, (state, action) => {
        const { restaurantId, data } = action.payload
        const item = state.items.find((i) => i.restaurantId === restaurantId)
        if (item !== undefined) {
          item.candidates = (data.candidates as MenuCandidate[]) ?? []
        }
      })
      .addCase(acceptMenuCandidate.fulfilled, (state, action) => {
        const { restaurantId, candidate } = action.payload
        const item = state.items.find((i) => i.restaurantId === restaurantId)
        const idx = item?.candidates?.findIndex((c) => c.candidateId === candidate.candidateId) ?? -1
        if (idx !== -1 && item?.candidates !== undefined) item.candidates[idx] = candidate
      })
      .addCase(rejectMenuCandidate.fulfilled, (state, action) => {
        const { restaurantId, candidate } = action.payload
        const item = state.items.find((i) => i.restaurantId === restaurantId)
        const idx = item?.candidates?.findIndex((c) => c.candidateId === candidate.candidateId) ?? -1
        if (idx !== -1 && item?.candidates !== undefined) item.candidates[idx] = candidate
      })
      .addCase(autoProcessAllMissingMenus.fulfilled, (state) => {
        state.status = 'idle'
      })
      .addCase(autoProcessAllMissingMenus.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(autoProcessAllMissingMenus.rejected, (state) => {
        state.status = 'error'
      })
  },
})

export const selectMissingMenuItems = (state: RootState): MissingMenuRestaurant[] =>
  state.missingMenuQueue.items
export const selectMissingMenuCount = (state: RootState): number => state.missingMenuQueue.count
export const selectMissingMenuPage = (state: RootState): number => state.missingMenuQueue.page
export const selectMissingMenuPageSize = (state: RootState): number =>
  state.missingMenuQueue.pageSize
export const selectMissingMenuIsLoading = (state: RootState): boolean =>
  state.missingMenuQueue.status === 'loading'

export const { setPage, setPageSize } = missingMenuQueueSlice.actions
export default missingMenuQueueSlice.reducer
